/* eslint-disable no-unused-expressions */
/* eslint-disable no-unused-expressions */

import axios from 'axios';
import { prodDb, subDb, userDb as profileDb } from '../../config/pouch';
import { setLoading, setMessage } from './status';
import { auth } from '../../config/firebase';

export const MONGO_SYNC_START = 'MONGO_SYNC_START';
export const MONGO_SYNC_PROGRESS = 'MONGO_SYNC_PROGRESS';
export const MONGO_SYNC_SUCCESS = 'MONGO_SYNC_SUCCESS';
export const MONGO_SYNC_FAILURE = 'MONGO_SYNC_FAILURE';

const API_MONGO_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';
const BATCH_SIZE = 50;
const REQUEST_DELAY = 1000;
const REQUEST_TIMEOUT = 30000;
const MAX_RETRIES = 3;

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Nettoie un document MongoDB pour le rendre compatible avec PouchDB
 */
const cleanMongoDocument = (doc) => {
    if (!doc || typeof doc !== 'object') return doc;

    const cleaned = { ...doc };

    const fieldsToRemove = [
        '__v', '_class', '$__', '$isNew', '_doc', 'isModified',
        'isNew', 'save', 'validate', 'toObject', 'toJSON',
        '$locals', '$op', '_pres', '_posts', 'schema', 'collection'
    ];

    fieldsToRemove.forEach(field => {
        delete cleaned[field];
    });

    Object.keys(cleaned).forEach(key => {
        if ((key.startsWith('$') || (key.startsWith('_') && key !== '_id' && key !== '_rev'))
            && typeof cleaned[key] === 'function') {
            delete cleaned[key];
        }
    });

    if (cleaned._id) {
        cleaned._id = cleaned._id.toString();
    }

    return cleaned;
};

/**
 * Vérifie que l'utilisateur est authentifié avant la synchronisation
 */
const checkAuthentication = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('Vous devez être connecté pour synchroniser les données hors ligne.');
    }
    return currentUser;
};

/**
 * Synchronise les produits depuis MongoDB vers PouchDB
 */
export const syncProductsFromMongoDB = (onProgress) => async dispatch => {
    try {
        await checkAuthentication();

        let offset = 0;
        let hasMore = true;
        let totalSynced = 0;

        while (hasMore) {
            let retryCount = 0;
            let batchSuccess = false;

            while (retryCount < MAX_RETRIES && !batchSuccess) {
                try {
                    if (retryCount > 0) {
                        await wait(REQUEST_DELAY * retryCount);
                    }

                    const response = await axios.get(`${API_MONGO_URL}/products`, {
                        params: { limit: BATCH_SIZE, offset: offset },
                        timeout: REQUEST_TIMEOUT
                    });

                    const products = response.data.products || [];

                    if (products.length === 0) {
                        hasMore = false;
                        batchSuccess = true;
                        break;
                    }

                    for (const product of products) {
                        try {
                            const cleanProduct = cleanMongoDocument(product);

                            const pouchDoc = {
                                _id: cleanProduct._id || cleanProduct.id,
                                ...cleanProduct,
                                syncedAt: new Date().toISOString(),
                                source: 'mongodb'
                            };

                            try {
                                const existing = await prodDb.get(pouchDoc._id);
                                pouchDoc._rev = existing._rev;
                            } catch (error) {}

                            await prodDb.put(pouchDoc);
                            totalSynced++;

                        } catch (error) {}
                    }

                    onProgress?.({
                        type: 'products',
                        current: totalSynced,
                        batch: products.length,
                        offset: offset
                    });

                    offset += BATCH_SIZE;
                    batchSuccess = true;

                    if (products.length < BATCH_SIZE) {
                        hasMore = false;
                    }

                    if (hasMore) {
                        await wait(REQUEST_DELAY);
                    }

                } catch (error) {
                    retryCount++;

                    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                        if (retryCount >= MAX_RETRIES) {
                            offset += BATCH_SIZE;
                            batchSuccess = true;
                        }
                    } else {
                        if (retryCount >= MAX_RETRIES) {
                            throw error;
                        }
                    }
                }
            }
        }

        return totalSynced;

    } catch (error) {
        throw error;
    }
};

/**
 * Synchronise les substances (DCI) en les extrayant des produits
 */
export const syncSubstancesFromMongoDB = (onProgress) => async dispatch => {
    try {
        await checkAuthentication();

        let allDCIs = new Set();
        let offset = 0;
        let hasMore = true;

        while (hasMore) {
            try {
                const response = await axios.get(`${API_MONGO_URL}/products`, {
                    params: { limit: BATCH_SIZE, offset: offset },
                    timeout: REQUEST_TIMEOUT
                });

                const products = response.data.products || [];

                if (products.length === 0) {
                    hasMore = false;
                    break;
                }

                products.forEach(product => {
                    if (product.DCI) {
                        const dcis = product.DCI.split(',').map(dci => dci.trim());
                        dcis.forEach(dci => {
                            if (dci && dci.length > 1) {
                                allDCIs.add(dci);
                            }
                        });
                    }
                });

                offset += BATCH_SIZE;

                if (products.length < BATCH_SIZE) {
                    hasMore = false;
                }

                await wait(REQUEST_DELAY);

            } catch (error) {
                offset += BATCH_SIZE;
            }
        }

        let totalSynced = 0;
        const dciArray = Array.from(allDCIs);

        for (const dci of dciArray) {
            try {
                const substanceDoc = cleanMongoDocument({
                    _id: dci.toLowerCase().replace(/[^a-z0-9]/g, '_'),
                    nom: dci,
                    name: dci,
                    DCI: dci,
                    syncedAt: new Date().toISOString(),
                    source: 'mongodb_extracted'
                });

                try {
                    const existing = await subDb.get(substanceDoc._id);
                    substanceDoc._rev = existing._rev;
                } catch (error) {}

                await subDb.put(substanceDoc);
                totalSynced++;

                onProgress?.({
                    type: 'substances',
                    current: totalSynced,
                    total: dciArray.length
                });

            } catch (error) {}
        }

        return totalSynced;

    } catch (error) {
        throw error;
    }
};

/**
 * Synchronise les formations depuis Firebase Firestore
 */
export const syncTrainingsFromFirebase = (onProgress) => async dispatch => {
    try {
        await checkAuthentication();

        const { firestore } = await import('../../config/firebase');

        const snapshot = await firestore.collection('trainings').get();
        const trainings = [];

        snapshot.forEach(doc => {
            const cleanTraining = cleanMongoDocument({
                _id: doc.id,
                id: doc.id,
                ...doc.data(),
                syncedAt: new Date().toISOString(),
                source: 'firebase'
            });
            trainings.push(cleanTraining);
        });

        const { getLocalDB } = await import('../../config/pouch');
        const trainingDb = getLocalDB('linfomed_trainings');

        let totalSynced = 0;

        for (const training of trainings) {
            try {
                try {
                    const existing = await trainingDb.get(training._id);
                    training._rev = existing._rev;
                } catch (error) {}

                await trainingDb.put(training);
                totalSynced++;

                onProgress?.({
                    type: 'trainings',
                    current: totalSynced,
                    total: trainings.length
                });

            } catch (error) {}
        }

        return totalSynced;

    } catch (error) {
        throw error;
    }
};

/**
 * Synchronise les profils utilisateur depuis Firebase
 */
export const syncProfilesFromFirebase = (onProgress) => async dispatch => {
    try {
        await checkAuthentication();

        const { firestore } = await import('../../config/firebase');

        const snapshot = await firestore.collection('profiles').get();
        const profiles = [];

        snapshot.forEach(doc => {
            const cleanProfile = cleanMongoDocument({
                _id: doc.id,
                id: doc.id,
                ...doc.data(),
                syncedAt: new Date().toISOString(),
                source: 'firebase'
            });
            profiles.push(cleanProfile);
        });

        let totalSynced = 0;

        for (const profile of profiles) {
            try {
                try {
                    const existing = await profileDb.get(profile._id);
                    profile._rev = existing._rev;
                } catch (error) {}

                await profileDb.put(profile);
                totalSynced++;

                onProgress?.({
                    type: 'profiles',
                    current: totalSynced,
                    total: profiles.length
                });

            } catch (error) {}
        }

        return totalSynced;

    } catch (error) {
        throw error;
    }
};

/**
 * Charge les données de fallback depuis des fichiers JSON locaux
 */
const fallbackDataLoad = async () => {
    try {
        const { loadDataFromLocalJson } = await import('./local');
        await loadDataFromLocalJson();

        return {
            products: 100,
            substances: 50,
            trainings: 0,
            profiles: 20,
            errors: [],
            source: 'fallback'
        };
    } catch (error) {
        throw error;
    }
};

/**
 * Synchronisation complète de toutes les données
 */
export const fullMongoDBSync = () => async dispatch => {
    try {
        dispatch({ type: MONGO_SYNC_START });
        dispatch(setLoading(true));

        const results = {
            products: 0,
            substances: 0,
            trainings: 0,
            profiles: 0,
            errors: []
        };

        const progressCallback = (progress) => {
            dispatch({
                type: MONGO_SYNC_PROGRESS,
                payload: progress
            });
        };

        try {
            results.products = await dispatch(syncProductsFromMongoDB(progressCallback));
        } catch (error) {
            results.errors.push(`Produits: ${error.message}`);

            if (error.message.includes('timeout') || error.message.includes('network')) {
                try {
                    const fallbackResult = await fallbackDataLoad();
                    results.products = fallbackResult.products;
                    results.substances = fallbackResult.substances;
                    results.profiles = fallbackResult.profiles;
                    results.errors.push('Utilisation des données de fallback (JSON local)');

                    await dispatch({ type: 'LOAD_FALLBACK_DATA' });

                    return results;
                } catch (fallbackError) {
                    results.errors.push(`Fallback échoué: ${fallbackError.message}`);
                }
            }
        }

        if (results.products > 0 && !results.errors.some(e => e.includes('fallback'))) {
            try {
                results.substances = await dispatch(syncSubstancesFromMongoDB(progressCallback));
            } catch (error) {
                results.errors.push(`Substances: ${error.message}`);
            }
        }

        try {
            results.trainings = await dispatch(syncTrainingsFromFirebase(progressCallback));
        } catch (error) {
            results.errors.push(`Formations: ${error.message}`);
        }

        if (!results.errors.some(e => e.includes('fallback'))) {
            try {
                results.profiles = await dispatch(syncProfilesFromFirebase(progressCallback));
            } catch (error) {
                results.errors.push(`Profils: ${error.message}`);
            }
        }

        const syncTimestamp = new Date().toISOString();
        localStorage.setItem('lastMongoDBSync', syncTimestamp);

        dispatch({
            type: MONGO_SYNC_SUCCESS,
            payload: {
                ...results,
                syncedAt: syncTimestamp
            }
        });

        const totalSynced = results.products + results.substances + results.trainings + results.profiles;
        let message = `✅ Synchronisation terminée: ${totalSynced} éléments synchronisés`;

        if (results.errors.length > 0) {
            message += `\n⚠️ Quelques erreurs: ${results.errors.join(', ')}`;
        }

        dispatch(setMessage({
            message,
            status: results.errors.length === 0 ? 'success' : 'warning'
        }));

        return results;

    } catch (error) {
        dispatch({
            type: MONGO_SYNC_FAILURE,
            payload: error.message
        });

        dispatch(setMessage({
            message: `❌ Échec de la synchronisation: ${error.message}`,
            status: 'danger'
        }));

        throw error;
    } finally {
        dispatch(setLoading(false));
    }
};

/**
 * Récupère les statistiques de synchronisation
 */
export const getSyncStats = () => async dispatch => {
    try {
        const stats = {};

        try {
            const prodInfo = await prodDb.info();
            stats.products = {
                count: prodInfo.doc_count,
                size: prodInfo.disk_size || 0
            };
        } catch (error) {
            stats.products = { count: 0, size: 0, error: error.message };
        }

        try {
            const subInfo = await subDb.info();
            stats.substances = {
                count: subInfo.doc_count,
                size: subInfo.disk_size || 0
            };
        } catch (error) {
            stats.substances = { count: 0, size: 0, error: error.message };
        }

        try {
            const profileInfo = await profileDb.info();
            stats.profiles = {
                count: profileInfo.doc_count,
                size: profileInfo.disk_size || 0
            };
        } catch (error) {
            stats.profiles = { count: 0, size: 0, error: error.message };
        }

        stats.lastSync = localStorage.getItem('lastMongoDBSync');

        return stats;

    } catch (error) {
        throw error;
    }
};

/**
 * Vide toutes les données hors ligne
 */
export const clearOfflineData = () => async dispatch => {
    try {
        dispatch(setLoading(true));

        const promises = [
            prodDb.destroy(),
            subDb.destroy(),
            profileDb.destroy()
        ];

        await Promise.all(promises);

        localStorage.removeItem('lastMongoDBSync');

        dispatch(setMessage({
            message: '✅ Toutes les données hors ligne ont été supprimées',
            status: 'success'
        }));

        return true;

    } catch (error) {
        dispatch(setMessage({
            message: `❌ Erreur lors de la suppression: ${error.message}`,
            status: 'danger'
        }));

        throw error;
    } finally {
        dispatch(setLoading(false));
    }
};